import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { deepOrange, green, yellow } from '@mui/material/colors';
import { AdminUser, AdminUserReports, AdminUserProductLine, AdminUserRole, AdminUserTab, VerifyOrUpdatedUser } from '../../../app/models/admin';
import { observer } from 'mobx-react-lite';
import { format } from 'date-fns';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, CircularProgress, CssBaseline, Grid, IconButton, Paper, SpeedDial, 
  SpeedDialAction, Stack, Switch } from '@mui/material';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { admin, basic, t1aAdmin, UserActivityStatus } from '../../../app/common/options/adminOptions';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useStore } from '../../../app/stores/store';
import { toast } from 'react-toastify';

interface Props {
  user: AdminUser;
}

const StyledSpeedDial = styled(SpeedDial)(({ theme }) => ({
  position: 'absolute',
  '&.MuiSpeedDial-directionUp, &.MuiSpeedDial-directionLeft': {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  '&.MuiSpeedDial-directionDown, &.MuiSpeedDial-directionRight': {
    top: theme.spacing(2),
    right: theme.spacing(1),
  },
}));

export default observer(function AppUserCard({ user }: Props) {

  const [toggleTabs, setToggleTabs] = useState(false);
  const [toggleRoles, setToggleRoles] = useState(false);
  const [toggleReports, setToggleReports] = useState(false);
  const [toggleProductLines, setToggleProductLines] = useState(false);
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const { adminStore } = useStore();
  const { setUserTabs, setUserRole, setUserProductionLines, setUserReport, setUserReportToggle, setUserPickUpToggle, verifyOrUpdateUser, verifyOrUserUpdating } = adminStore;

  if (!user) return null;

  const actions = [
    {
      icon: !user.approved
        ? <PersonAddAltIcon />
        : <PersonOffIcon />, name: !user.approved ? 'Active' : 'Block'
    },
    { icon: <PersonRemoveIcon />, name: 'Delete' },
  ];

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleTabDelete = (email: string, tab: AdminUserTab) => {
    setUserTabs(email, tab)
  };

  const handleProductLineDelete = (email: string, line: AdminUserProductLine) => {
    setUserProductionLines(email, line)
  };

  const handleReportDelete = (email: string, report: AdminUserReports) => {
    setUserReport(email, report);
  };

  const handleRoleDelete = (email: string, role: AdminUserRole) => {
    setUserRole(email, role);
  };

  const handleToggleReport = (email: string) => {
    setToggleReports(false);
    setUserReportToggle(email);
  }

  const handleUserUpdate = (user: AdminUser) => {
    const roles = user.roles.filter(x => x.active);
    const tabs = user.tabs;
    const lines = user.lines;
    const report = user.userReports;

    if (roles.length >= 0) {
      const uptUser: VerifyOrUpdatedUser = {
        email: user.email,
        pickUp: user.pickUp,
        reporting: user.reporting,
        role: roles[0].roleName,
        tabs: tabs,
        lines: lines,
        userReports: report
      };
      verifyOrUpdateUser(uptUser);
      setToggleRoles(false);
      setToggleTabs(false);
      setToggleProductLines(false);
      setToggleReports(false);
    } else {
      toast.error('User must have a role')
    }
  }

  return (
    <Box component="div" sx={{ width: '100%', transform: 'translateZ(0px)', flexGrow: 1 }}>
      <CssBaseline />
      <Grid container spacing={2} direction="column" justifyContent="center" alignItems="center">
        <Grid item xs={12} sx={{ position: 'relative', pb: 0.5, pr: 0.5, mt: 2, bgcolor: '#234f1e', borderRadius: '100% 0% 29% 71% / 0% 100% 0% 100% ' }} >
          <Typography component='span' variant='body2' sx={{ position: 'absolute', right: 6, top: 0.1, color: '#fff' }}>
            Tier1Asset
          </Typography>
          <Card sx={{ width: { xs: 360, sm: 500 }, bgcolor: '#93f4d8', color: '#234f1e' }}>
            <CardHeader
              avatar={
                <Avatar src={user.picUrl}
                  sx={user.role === basic ? { bgcolor: deepOrange[500], width: 56, height: 56 }
                    : user.role === admin ? { bgcolor: green[500], width: 56, height: 56 }
                      : { bgcolor: yellow[500], width: 56, height: 56 }} aria-label="adminuser">
                  {user.fullName[0].toUpperCase()}
                </Avatar>
              }
              action={user.role !== t1aAdmin &&
                <StyledSpeedDial
                  ariaLabel="Admin Speed dial"
                  hidden={verifyOrUserUpdating ? true : false}
                  icon={<SpeedDialIcon />}
                  direction={'down'}
                >
                  {actions.map((action) => (
                    <SpeedDialAction
                      key={action.name}
                      icon={action.icon}
                      tooltipTitle={action.name}
                    />
                  ))}
                </StyledSpeedDial>
              }
              title={user.fullName.toUpperCase()}
              subheader={user.email}
            />
            <CardContent>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1bh-content"
                      id="panel1bh-header"
                    >
                      <Typography sx={{ width: '33%', flexShrink: 0 }}>
                        General Info
                      </Typography>
                      <Typography sx={{ color: 'text.secondary' }}>{`Status: ${user.activity || UserActivityStatus(user)}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Join Date:&nbsp;</strong>{`${format(new Date(user.joinDate), 'dd MMM yyyy')}`}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Emp. Type:&nbsp;</strong>{user.employeeType}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Country:&nbsp;</strong>{user.country}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Street:&nbsp;</strong>{user.street}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Zip Code:&nbsp;</strong>{user.zipCode}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>VAT No:&nbsp;</strong>{user.vatNumber}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant='body2'>
                            <strong>Company Info:&nbsp;</strong>{user.companyName}
                          </Typography>
                        </Grid>
                      </Grid>

                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Stack direction={"row"} spacing={1}>
                      <Typography variant="body1" color="text.secondary">
                        <strong>Test Result:</strong>
                      </Typography>
                      <Chip label={user.testResult} variant='outlined' />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Stack direction={"row"} spacing={1}>
                      <Typography variant="body1" color="text.secondary">
                        <strong>Role:</strong>
                      </Typography>
                      <Chip label={user.role} />
                      {user.role !== t1aAdmin &&
                        <IconButton disabled={verifyOrUserUpdating} onClick={() => setToggleRoles(!toggleRoles)}><EditIcon /></IconButton>}
                    </Stack>
                    <Stack direction={"row"} spacing={1}>
                      {toggleRoles && user.roles.map((role, index) => (
                        <Chip
                          key={role.roleName}
                          label={role.roleName}
                          onClick={() => handleRoleDelete(user.email, role)}
                          onDelete={() => handleRoleDelete(user.email, role)}
                          deleteIcon={role.active ? <DeleteIcon /> : <DoneIcon />}
                          variant={index % 2 === 0 ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>

                {/* Tabs */}
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Typography variant="body1" color="text.secondary">
                      <strong>Tabs:</strong>&nbsp;&nbsp;
                      {user.role !== t1aAdmin &&
                        <IconButton disabled={verifyOrUserUpdating} onClick={() => setToggleTabs(!toggleTabs)}>
                          {!toggleTabs ? <EditIcon /> : <DoneIcon />}
                        </IconButton>}
                    </Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      <Grid item xs={12}>
                        {!toggleTabs && user.tabs!.filter(x => x.active).map((tab, index) => (
                          <Chip sx={{ mr: 2, mt: 1 }} key={tab.tabName} label={tab.tabName} variant={index % 2 === 0 ? 'filled' : 'outlined'} />
                        ))}
                        {toggleTabs && user.tabs.map((tab, index) => (
                          <Chip
                            sx={{ mr: 2, mt: 1 }}
                            key={tab.tabName}
                            label={tab.tabName}
                            onClick={() => handleTabDelete(user.email, tab)}
                            onDelete={() => handleTabDelete(user.email, tab)}
                            deleteIcon={tab.active ? <DeleteIcon /> : <DoneIcon />}
                            variant={index % 2 === 0 ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* product lines */}
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Typography variant="body1" color="text.secondary">
                      <strong>ProductLines:</strong>&nbsp;&nbsp;
                      {user.role !== t1aAdmin &&
                        <IconButton disabled={verifyOrUserUpdating} onClick={() => setToggleProductLines(!toggleProductLines)}>
                          {!toggleProductLines ? <EditIcon /> : <DoneIcon />}
                        </IconButton>}
                    </Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      <Grid item xs={12}>
                        {!toggleProductLines && user.lines!.filter(x => x.active).map((line, index) => (
                          <Chip sx={{ mr: 2, mt: 1 }} key={line.line} label={line.line} variant={index % 2 === 0 ? 'filled' : 'outlined'} />
                        ))}
                        {toggleProductLines && user.lines.map((line, index) => (
                          <Chip
                            sx={{ mr: 2, mt: 1 }}
                            key={line.line}
                            label={line.line}
                            onClick={() => handleProductLineDelete(user.email, line)}
                            onDelete={() => handleProductLineDelete(user.email, line)}
                            deleteIcon={line.active ? <DeleteIcon /> : <DoneIcon />}
                            variant={index % 2 === 0 ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                {/* User Report  */}
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Typography variant="body1" color="text.secondary">
                      <strong>User Report:</strong>
                      <Switch
                        disabled={user.role === t1aAdmin}
                        checked={user.reporting}
                        onChange={() => handleToggleReport(user.email)}
                        inputProps={{ 'aria-label': 'Report' }}
                      />&nbsp;&nbsp;
                      {user.role !== t1aAdmin && user.reporting &&
                        <IconButton disabled={verifyOrUserUpdating} onClick={() => setToggleReports(!toggleReports)}>
                          {!toggleReports ? <EditIcon /> : <DoneIcon />}
                        </IconButton>}
                    </Typography>
                    <Grid container direction='row' wrap='wrap' rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      <Grid item xs={12}>
                        {!toggleReports && user.reporting && user.userReports!.filter(x => x.active).map((report, index) => (
                          <Chip sx={{ mr: 2, mt: 1 }} key={report.reportName} label={report.reportName} variant={index % 2 === 0 ? 'filled' : 'outlined'} />
                        ))}
                        {toggleReports && user.reporting && user.userReports!.map((report, index) => (
                          <Chip
                            sx={{ mr: 2, mt: 1 }}
                            key={report.reportName}
                            label={report.reportName}
                            onClick={() => handleReportDelete(user.email, report)}
                            onDelete={() => handleReportDelete(user.email, report)}
                            deleteIcon={report.active ? <DeleteIcon /> : <DoneIcon />}
                            variant={index % 2 === 0 ? 'filled' : 'outlined'}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                {/* User Pick ups  */}
                <Grid item xs={12}>
                  <Paper sx={{ padding: 1, color: 'secondary' }}>
                    <Typography variant="body1" color="text.secondary">
                      <strong>PickUps:</strong>
                      <Switch
                        disabled={user.role === t1aAdmin}
                        checked={user.pickUp}
                        onChange={() => setUserPickUpToggle(user.email)}
                        inputProps={{ 'aria-label': 'PickUp' }}
                      />
                    </Typography>
                  </Paper>
                </Grid>

              </Grid>
              <Box component="div" sx={{ mt: 3 }}>
                {verifyOrUserUpdating ? <CircularProgress /> :
                  <Button disabled={!user.isUpdated || verifyOrUserUpdating}
                    onClick={() => handleUserUpdate(user)}
                    variant="contained">{!user.isUpdated ? "Saved" : "Save"}</Button>
                }
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box >
  );
})